// API Configuration
// In production, Firebase Functions are served from the same domain via rewrites
// Always use relative path /api for production (Firebase Hosting rewrites to Functions)
const getApiBaseUrl = () => {
  // Force use of relative path in production (ignore REACT_APP_API_URL if set incorrectly)
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  // Development: use environment variable or localhost
  return process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
};

export const API_BASE_URL = getApiBaseUrl();

// Helper function to get full API URL
export const getApiUrl = (endpoint) => {
  // Remove leading slash if present
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${API_BASE_URL}/${cleanEndpoint}`;
};

// Helper function for making API calls with error handling
export const apiCall = async (endpoint, options = {}) => {
  try {
    const response = await fetch(getApiUrl(endpoint), {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    // Handle non-OK responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        success: false,
        message: `Server error: ${response.status} ${response.statusText}`,
      }));
      return errorData;
    }

    return await response.json();
  } catch (error) {
    return { success: false, message: 'Network error. Please try again.' };
  }
};

// Save client data from contact form, cost calculator, or popup
export const saveClientData = async (data) => {
  try {
    const response = await fetch(`${API_BASE_URL}/client-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();
    return result;
  } catch (error) {
    return { success: false, message: 'Failed to save data' };
  }
};

// Admin API functions
export const adminLogin = async (email, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        success: false,
        message: `Server error: ${response.status}`,
      }));
      return errorData;
    }

    return await response.json();
  } catch (error) {
    return { success: false, message: 'Network error. Please try again.' };
  }
};

export const adminApiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('adminToken');
  
  // Don't set Content-Type for FormData (browser will set it with boundary)
  const isFormData = options.body instanceof FormData;
  
  // Build headers - don't include Content-Type for FormData
  const headers = {
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {}),
  };
  
  // Only add Content-Type if it's NOT FormData and not already set
  if (!isFormData && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: options.method || 'GET',
      headers: headers,
      body: options.body,
      ...(options.credentials ? { credentials: options.credentials } : {}),
    });

    // Check if response is OK
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        success: false,
        message: `Server error: ${response.status} ${response.statusText}`,
      }));
      return errorData;
    }

    return await response.json();
  } catch (error) {
    console.error('API call error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
};

// Client API call function (for authenticated client requests)
export const clientApiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('clientToken');
  
  if (!token) {
    return { success: false, message: 'Not authenticated. Please log in.' };
  }

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(getApiUrl(endpoint), {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        success: false,
        message: `Server error: ${response.status} ${response.statusText}`,
      }));
      return { ...errorData, status: response.status };
    }

    return await response.json();
  } catch (error) {
    return { success: false, message: 'Network error. Please try again.' };
  }
};

